import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface BingConfig {
  trackingId: string;
  isEnabled?: boolean;
}

export class BingProvider implements Provider {
  type = ProviderType.BING;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: BingConfig;

  constructor(config: BingConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // Initialize Bing UET tracking
      // eslint-disable-next-line prefer-destructuring
      const trackingId = this.config.trackingId;
      // 等待页面加载完成
      await new Promise((resolve) => {
        if (document.readyState === 'complete') {
          resolve(true);
        }
        window.addEventListener('load', function () {
          resolve(true);
        });
      });
      // Bing UET initialization script
      return new Promise((resolve) => {
        const w = window;
        const d = document;
        const t = 'script';
        const r = '//bat.bing.com/bat.js';
        const u = 'uetq';

        // Initialize uetq array if not exists
        w[u] = w[u] || [];

        const f = () => {
          const o = { ti: trackingId };
          (o as any).q = w[u];
          w[u] = new w.UET(o);
          w[u].push('pageLoad');
          this.initialized = true;
          resolve(true);
        };

        const n = d.createElement(t) as HTMLScriptElement;
        n.src = r;
        n.async = true;
        n.onload = function () {
          f();
          n.onload = null;
        };
        (n as any).onreadystatechange = function () {
          const s = (this as any).readyState;
          if (s && s !== 'loaded' && s !== 'complete') return;
          f();
          (n as any).onreadystatechange = null;
        };

        document.body.appendChild(n);
      });
    } catch (error) {
      logger.error('Failed to initialize Bing UET:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      if (window.uetq && typeof window.uetq.push === 'function') {
        if (this.loggingEnabled) {
          logger.info('Bing UET tracking event:', event);
        }
        (window.uetq as any).push('config', 'tcf', { enabled: true });
        // Track custom event with Bing UET event.type 有传空字符串的情况
        if (event.eventName != null && event.properties) {
          (window.uetq as any).push(
            event.commandType || 'event',
            event.eventName,
            event.properties,
          );
        } else if (event.eventName) {
          window.uetq.push(event.commandType || 'event', event.eventName);
        }
      }
    } catch (error) {
      logger.error('Bing UET tracking error:', error);
    }
  }

  /**
   * 启用Bing日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Bing日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
